import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { StrategyGuidanceBanner } from '../strategy/StrategyGuidanceBanner';
import { NumericInput } from '../strategy/NumericInput';
import { DraftSettingsPanel } from './DraftSettingsPanel';
import { ReleaseTable } from './ReleaseTable';
import { TrainingOverviewTab } from './TrainingOverviewTab';
import { COLORS } from '../../constants/theme';
import { formatNumber } from '../../utils/format';
import { TRAINABLE_UNITS } from '../../constants/trainableUnits';
import { computeUnitDeficit } from '../../utils/unitStockMath';
import type { TrainingShellProps, DesktopTrainingTab } from '../../pages/Training';
import type { TrainableUnitType } from '../../types/train';

const TABS: readonly { key: DesktopTrainingTab; label: string }[] = [
  { key: 'train', label: 'Train' },
  { key: 'release', label: 'Release' },
  { key: 'overview', label: 'Overview' },
];

/**
 * SF-141: thin wrapper around the existing (already desktop-appropriate) Training JSX — no layout
 * redesign. Absorbs `TrainTable` (previously an inline function at the bottom of `Training.tsx`,
 * the only one of the three tab-content pieces not already its own file) with its two dead props
 * (`province`, `raceConfig`) dropped — they weren't referenced in the function body (Phase 3
 * Decisions §17). Adds a real, focus-managed `<h1>`, matching the other three migrated pages.
 *
 * SF-162: adds a third tab (Overview) and full WAI-ARIA roles/attrs to the Train/Release strip,
 * matching `DesktopSciencesShell.tsx`'s exact pattern — a **roles-only** retrofit (no roving
 * `tabIndex`/arrow-key navigation, matching that precedent's own gap — Component 3).
 */
export function DesktopTrainingShell({
  province,
  activeStrategy,
  strategyLoading,
  desktopActiveTab,
  setDesktopActiveTab,
  trainQty,
  accelerated,
  setAccelerated,
  isSubmitting,
  submitError,
  successMessage,
  bulkMutationVersion,
  releaseQty,
  setReleaseQty,
  draftRate,
  setDraftRate,
  draftTarget,
  setDraftTarget,
  isSubmittingOptions,
  optionsError,
  optionsSuccess,
  draftLoadMessage,
  raceConfig,
  draftRates,
  maxDraftTarget,
  totalPop,
  draftCapacityPop,
  goldAvailable,
  soldiers,
  peasants,
  draftPct,
  draftTargetCount,
  trainingTime,
  hasAnyArmyComp,
  totalCost,
  totalTraining,
  getTarget,
  getTargetOrNull,
  getOwnedCount,
  getInTrainingCount,
  getTrainingCost,
  getUnitName,
  getUnitStats,
  handleTrainInputChange,
  handleOptimizeAll,
  handleOptimizeSingleUnit,
  handleClearAll,
  handleTrainSubmit,
  handleLoadDraftFromStrategy,
  handleDraftSave,
  handleReleaseBulkMutation,
  worldId,
  provinceId,
  refresh,
}: TrainingShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Military
      </h1>

      {/* Strategy Guidance Banner */}
      <StrategyGuidanceBanner activeStrategy={activeStrategy} isLoading={strategyLoading} page="military" />

      {/* ── Stat Cards Row ─────────────────────────────────── */}
      <div className="flex gap-3">
        <div className="w-36">
          <StatCard
            label="Gold"
            value={`${formatNumber(goldAvailable)}gc`}
            sub="available"
            color={COLORS.gold}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Soldiers"
            value={formatNumber(soldiers)}
            sub="for specs & elites"
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Peasants"
            value={formatNumber(peasants)}
            sub={`of ${formatNumber(totalPop)} pop`}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Draft Level"
            value={`${draftPct}%`}
            sub={`${formatNumber(soldiers)} / ${formatNumber(draftTargetCount)} · of ${formatNumber(draftCapacityPop)}`}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Training Time"
            value={`${trainingTime} days`}
            sub={accelerated ? 'accelerated' : 'standard'}
            borderless
          />
        </div>
      </div>

      {/* ── Draft Settings (always visible) ──────────────────── */}
      <DraftSettingsPanel
        draftRate={draftRate}
        draftTarget={draftTarget}
        draftRates={draftRates}
        maxDraftTarget={maxDraftTarget}
        isSubmitting={isSubmittingOptions}
        error={optionsError}
        success={optionsSuccess}
        onDraftRateChange={setDraftRate}
        onDraftTargetChange={setDraftTarget}
        onSave={handleDraftSave}
        activeStrategy={activeStrategy}
        onLoadFromStrategy={handleLoadDraftFromStrategy}
        draftLoadMessage={draftLoadMessage}
      />

      {/* ── Train / Release / Overview Tabs ───────────────────── */}
      <div className="flex border-b" style={{ borderColor: COLORS.border }} role="tablist">
        {TABS.map((tab) => {
          const isActive = desktopActiveTab === tab.key;
          return (
            <button
              key={tab.key}
              id={`training-desktop-tab-${tab.key}`}
              role="tab"
              aria-selected={isActive}
              aria-controls={`training-desktop-panel-${tab.key}`}
              onClick={() => setDesktopActiveTab(tab.key)}
              className="px-4 py-2 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderBottomColor: isActive ? COLORS.text.secondary : 'transparent',
                color: isActive ? COLORS.text.secondary : COLORS.text.tertiary,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {desktopActiveTab === 'train' && (
        <div role="tabpanel" id="training-desktop-panel-train" aria-labelledby="training-desktop-tab-train">
          <TrainTable
            trainQty={trainQty}
            accelerated={accelerated}
            hasAnyArmyComp={hasAnyArmyComp}
            getTarget={getTarget}
            getOwnedCount={getOwnedCount}
            getInTrainingCount={getInTrainingCount}
            getTrainingCost={getTrainingCost}
            getUnitName={getUnitName}
            getUnitStats={getUnitStats}
            totalCost={totalCost}
            totalTraining={totalTraining}
            goldAvailable={goldAvailable}
            soldiers={soldiers}
            peasants={peasants}
            isSubmitting={isSubmitting}
            submitError={submitError}
            successMessage={successMessage}
            onInputChange={handleTrainInputChange}
            onOptimizeSingleUnit={handleOptimizeSingleUnit}
            onOptimizeAll={handleOptimizeAll}
            onClearAll={handleClearAll}
            onSubmit={handleTrainSubmit}
            onAcceleratedChange={setAccelerated}
          />
        </div>
      )}

      {desktopActiveTab === 'release' && (
        <div role="tabpanel" id="training-desktop-panel-release" aria-labelledby="training-desktop-tab-release">
          <ReleaseTable
            province={province}
            raceConfig={raceConfig}
            worldId={worldId!}
            provinceId={provinceId!}
            releaseQty={releaseQty}
            onReleaseQtyChange={setReleaseQty}
            onSuccess={refresh}
            activeStrategy={activeStrategy}
            getTargetOrNull={getTargetOrNull}
            resetKey={bulkMutationVersion}
            onBulkMutation={handleReleaseBulkMutation}
          />
        </div>
      )}

      {desktopActiveTab === 'overview' && (
        <div role="tabpanel" id="training-desktop-panel-overview" aria-labelledby="training-desktop-tab-overview">
          <TrainingOverviewTab province={province} getUnitStats={getUnitStats} getUnitName={getUnitName} compact={false} />
        </div>
      )}
    </div>
  );
}

// ─── TrainTable (absorbed from Training.tsx, SF-141) ────────────────────────

interface TrainTableProps {
  trainQty: Record<string, number>;
  accelerated: boolean;
  hasAnyArmyComp: boolean;
  getTarget: (unitType: TrainableUnitType) => number;
  getOwnedCount: (unitType: string) => number;
  getInTrainingCount: (unitType: string) => number;
  getTrainingCost: (unitType: string) => number;
  getUnitName: (unitType: string, unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null) => string;
  getUnitStats: (unitType: string, unitKey: 'offenseSpecialist' | 'defenseSpecialist' | 'elite' | null) => { offense: number; defense: number };
  totalCost: number;
  totalTraining: number;
  goldAvailable: number;
  soldiers: number;
  peasants: number;
  isSubmitting: boolean;
  submitError: string | null;
  successMessage: string | null;
  onInputChange: (unitType: string, value: string) => void;
  onOptimizeSingleUnit: (unitType: TrainableUnitType) => void;
  onOptimizeAll: () => void;
  onClearAll: () => void;
  onSubmit: () => void;
  onAcceleratedChange: (value: boolean) => void;
}

function TrainTable({
  trainQty,
  accelerated,
  hasAnyArmyComp,
  getTarget,
  getOwnedCount,
  getInTrainingCount,
  getTrainingCost,
  getUnitName,
  getUnitStats,
  totalCost,
  totalTraining,
  goldAvailable,
  soldiers,
  peasants,
  isSubmitting,
  submitError,
  successMessage,
  onInputChange,
  onOptimizeSingleUnit,
  onOptimizeAll,
  onClearAll,
  onSubmit,
  onAcceleratedChange,
}: TrainTableProps) {
  const gridTemplate = '1fr 64px 64px 70px 114px';

  return (
    <>
      {/* Table */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
      {/* Table header */}
      <div
        className="grid items-center px-3 py-1.5"
        style={{
          gridTemplateColumns: gridTemplate,
          backgroundColor: COLORS.sectionHeader,
        }}
      >
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.primary }}>Unit (Off/Def)</span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>HAVE</span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>IN PROG</span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>COST</span>
        <span className="text-right text-xs uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>TRAIN</span>
      </div>

      {/* Table rows */}
      {TRAINABLE_UNITS.map((unit, rowIndex) => {
        const owned = getOwnedCount(unit.type);
        const inTraining = Math.floor(getInTrainingCount(unit.type));
        const cost = getTrainingCost(unit.type);
        const displayCost = accelerated ? cost * 2 : cost;
        const stats = getUnitStats(unit.type, unit.key);
        const unitName = getUnitName(unit.type, unit.key);
        const displayName = `${unitName} (${stats.offense}/${stats.defense})`;
        const qty = trainQty[unit.type] || 0;

        const target = getTarget(unit.type);
        const hasTarget = target > 0;
        const { deficit } = computeUnitDeficit({ target, owned, inProgress: inTraining });
        const maxByGold = displayCost > 0 ? Math.floor(goldAvailable / displayCost) : 0;
        const canOptimize = hasTarget && deficit > 0 && maxByGold > 0 &&
          (unit.type === 'THIEF' ? peasants > 0 : soldiers > 0);

        return (
          <div
            key={unit.type}
            className="grid items-center px-3 py-1.5"
            style={{
              gridTemplateColumns: gridTemplate,
              borderBottom: `1px solid ${COLORS.border}`,
              backgroundColor: rowIndex % 2 === 0 ? COLORS.tableRowAlt : COLORS.card,
            }}
          >
            <span className="text-sm" style={{ color: COLORS.text.primary }}>{displayName}</span>
            <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>{formatNumber(owned)}</span>
            <span className="text-right font-mono text-sm" style={{ color: inTraining > 0 ? COLORS.text.secondary : COLORS.text.tertiary }}>
              {inTraining > 0 ? formatNumber(inTraining) : '—'}
            </span>
            <span className="text-right font-mono text-sm" style={{ color: COLORS.text.secondary }}>{formatNumber(displayCost)}gc</span>
            <div className="flex items-center justify-end gap-1">
              {canOptimize && (
                <button
                  onClick={() => onOptimizeSingleUnit(unit.type)}
                  aria-label={`Optimize ${unitName} to target`}
                  style={{
                    width: '26px',
                    height: '26px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.sectionHeader,
                    color: '#B89878',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  ⚡
                </button>
              )}
              <NumericInput
                value={qty}
                onChange={(v) => onInputChange(unit.type, String(v))}
                className="w-16"
                ariaLabel={`Train ${unitName} quantity`}
              />
            </div>
          </div>
        );
      })}


        {/* Submit Bar */}
        <div
          className="px-4 py-3 space-y-3"
          style={{ borderTop: `1px solid ${COLORS.border}` }}
        >
        {submitError && (
          <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
        )}
        {successMessage && (
          <div className="text-sm" style={{ color: COLORS.positive }}>{successMessage}</div>
        )}

        <div className="flex items-center gap-6 text-sm">
          <span className="whitespace-nowrap">
            <span style={{ color: COLORS.text.tertiary }}>Training: </span>
            <span className="font-mono" style={{ color: COLORS.text.primary }}>
              {formatNumber(totalTraining)} units
            </span>
          </span>
          <span className="whitespace-nowrap">
            <span style={{ color: COLORS.text.tertiary }}>Total Cost: </span>
            <span className="font-mono" style={{ color: totalCost > goldAvailable ? COLORS.negative : totalCost > 0 ? COLORS.gold : COLORS.text.tertiary }}>
              {formatNumber(totalCost)} gc
            </span>
          </span>
        </div>

        <div className="flex items-center gap-4">
          {hasAnyArmyComp && (
            <button
              onClick={onOptimizeAll}
              className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded"
              style={{
                backgroundColor: COLORS.sectionHeader,
                border: `1px solid ${COLORS.border}`,
                color: '#B89878',
                cursor: 'pointer',
              }}
            >
              ⚡ Fill Strategy
            </button>
          )}

          <label
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            style={{ color: COLORS.text.secondary }}
          >
            <input
              type="checkbox"
              checked={accelerated}
              onChange={(e) => onAcceleratedChange(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            Accelerated (2× cost, ½ time)
          </label>

          <div className="flex gap-2 ml-auto">
            <Button variant="secondary" onClick={onClearAll} disabled={totalTraining === 0}>
              Clear All
            </Button>
            <Button onClick={onSubmit} isLoading={isSubmitting} disabled={totalTraining === 0}>
              Train Units
            </Button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
